import TextInput from './TextInput';
import { useState, useRef, forwardRef, useImperativeHandle, useCallback, useEffect } from 'react';
import { useGridCellEditor } from 'ag-grid-react';

const TextFieldEditor = forwardRef(
  ({ value, column, onValueChange, onChange }, ref) => {
    const [inputValue, setInputValue] = useState(value ?? '');
    const inputRef = useRef();
    const isEditingCompletedRef = useRef(false);
    
    const focusOnInput = () => {
      if (inputRef?.current) {
        inputRef.current?.focus?.();
      }
    };

    useEffect(() => {
      setInputValue(value ?? '');
      focusOnInput();
    }, [column?.colDef?.field]);

    const getFormattedGridValue = useCallback(() => {
      const format = column?.colDef?.cellEditorParams?.format;
      if (format && inputValue !== value) {
        return format.test(inputValue) ? inputValue : null;
      }
      return inputValue;
    }, [value, inputValue, column]);

    useImperativeHandle(ref, () => ({
      getValue: () => getFormattedGridValue(),
      focusOnInput
    }));

    const afterGuiAttached = useCallback(() => {
      focusOnInput();
    }, []);

    const isCancelAfterEnd = useCallback(() => {
      if (isEditingCompletedRef.current) return true; // Returning true to avoid setting new value
      isEditingCompletedRef.current = true; // prevent further commits
      const formattedValue = getFormattedGridValue();
      onValueChange(formattedValue);
      return false;
    }, [onValueChange, getFormattedGridValue]);

    useGridCellEditor({
      isCancelAfterEnd,
      afterGuiAttached
    });

    const handleInputChange = (event) => {
      let updatedValue = event?.target?.value ?? '';
      if (column?.colDef?.cellEditorParams?.capitalize) {
        updatedValue = updatedValue.trim().toUpperCase();
      }
      setInputValue(updatedValue);
      onChange?.(updatedValue);
    };

    const editorParams = column?.colDef?.cellEditorParams || {};

    return (
      <TextInput
        onChange={handleInputChange}
        value={inputValue}
        inputRef={inputRef}
        {...editorParams}
      />
    );
  }
);

export default TextFieldEditor;
